import React from 'react';
import {
  Avatar,
  Box,
  IconButton,
  Icons,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import {
  ExtensionStudent,
  ManageExtensionStyles,
} from './ManageExtension.types';

type Props = {
  canAdd?: boolean;
  isDimmed?: boolean;
  isSelected: boolean;
  onToggle: (studentId: string) => void;
  showAction?: boolean;
  student: ExtensionStudent;
  styles: ManageExtensionStyles;
  extensionName?: string;
  isStudentStep?: boolean;
};

const StudentRow = ({
  canAdd = false,
  isDimmed = false,
  isSelected,
  onToggle,
  showAction = true,
  student,
  styles,
  extensionName,
  isStudentStep = false,
}: Props) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [nameRef, isNameOverflowing] = useIsOverflowing<HTMLDivElement>();
  const [emailRef, isEmailOverflowing] = useIsOverflowing<HTMLDivElement>();

  const { fullName, lastName } = student;

  if (!fullName) {
    return null;
  }

  const fallbackText =
    fullName
      .slice(0, 1)
      .toUpperCase()
      .concat(lastName?.slice(0, 1).toUpperCase() ?? '') ?? 'AA';

  return (
    <Box component="li" sx={styles.studentRow}>
      <Avatar
        src={student.avatar}
        sx={{
          ...styles.avatar,
          ...(isDimmed && styles.addedStudentAvatar),
        }}
        fallbackText={fallbackText}
      />
      <Box sx={styles.studentDetails}>
        <Box sx={styles.studentName}>
          <Tooltip
            title={`${student.fullName || ''}${isSelected ? ' - Added' : ''}`}
            disableFocusListener={!isMdUp || !isNameOverflowing}
            disableHoverListener={!isMdUp || !isNameOverflowing}
            disableTouchListener={!isMdUp || !isNameOverflowing}
          >
            <Box
              ref={nameRef}
              sx={{
                ...styles.studentNameText,
                ...(isDimmed && styles.addedStudentText),
              }}
            >
              {student.fullName}
            </Box>
          </Tooltip>
          {isSelected && (
            <Box
              display="flex"
              alignItems="center"
              flexShrink={0}
              sx={{ whiteSpace: 'nowrap' }}
            >
              <Box
                component="span"
                sx={{ color: isDimmed ? 'text.secondary' : 'text.primary' }}
              >
                &nbsp;-&nbsp;
              </Box>
              <Box component="span" sx={styles.added}>
                Added
              </Box>
            </Box>
          )}
        </Box>
        <Tooltip
          title={student.email || ''}
          disableFocusListener={!isMdUp || !isEmailOverflowing}
          disableHoverListener={!isMdUp || !isEmailOverflowing}
          disableTouchListener={!isMdUp || !isEmailOverflowing}
        >
          <Box ref={emailRef} sx={styles.studentEmail}>
            {student.email}
          </Box>
        </Tooltip>
      </Box>
      {showAction && (
        <IconButton
          type="button"
          aria-label={`${canAdd && !isSelected ? 'Add' : 'Remove'} ${
            student.fullName
          }`}
          onClick={() => onToggle(student.id)}
          sx={canAdd && !isSelected ? styles.addStudent : styles.removeStudent}
        >
          <Tooltip
            disableFocusListener={!isMdUp || isStudentStep}
            disableHoverListener={!isMdUp || isStudentStep}
            disableTouchListener={!isMdUp || isStudentStep}
            title={
              canAdd && !isSelected
                ? `Add to ${extensionName ? extensionName : 'this extension'}`
                : `Remove from ${
                    extensionName ? extensionName : 'this extension'
                  }`
            }
          >
            {canAdd && !isSelected ? (
              <Icons.AddCircle />
            ) : (
              <Icons.RemoveCircle />
            )}
          </Tooltip>
        </IconButton>
      )}
    </Box>
  );
};

export default StudentRow;
